/**
 * Sort options and filter helpers for the Tasks list, extracted from the page
 * so the parts that are actually LOGIC can be unit tested without mounting a
 * calendar.
 *
 * Tasks is the odd CRM list page: it renders CARDS, not a table, so there are
 * no column headers to click and no `SortableHeader`. The same server sort keys
 * are offered through a "Sort by" dropdown instead, and `TASKS_TABLE.columns`
 * stays the single list they come from — a key that drifts from
 * `Api::V1::TasksController.sortable_columns` fails
 * `components/table/config-parity.test.ts` here and
 * `declared_keys_export_test.rb` in crm-api, exactly as it does for the five
 * table pages.
 */
import { format } from "date-fns";

import type { TableColumn } from "@crm-src/components/table/configs";
import { TASKS_TABLE } from "@crm-src/components/table/configs";
import type { Task } from "@crm-src/types/task";

/**
 * The facet keys the Tasks page keeps in the URL.
 *
 * They are DELIBERATELY not `TASKS_TABLE.facetKeys`, which is `[]` and must
 * stay `[]`: `server-keys.json`'s `tasks.facets` is empty because
 * `Api::V1::TasksController` declares NO server-side facets. Status and
 * priority are applied IN MEMORY over the already-fetched card list (see
 * `matchesTaskFilters`), and this list only tells `useTableQuery` which query
 * params to round-trip — nothing here is ever sent to the API, so declaring it
 * cannot put the client and server allow-lists out of step.
 */
export const TASKS_CLIENT_FACET_KEYS = ["status", "priority"] as const;

export type TasksClientFacetKey = (typeof TASKS_CLIENT_FACET_KEYS)[number];

/** The sentinel the two dropdowns use for "don't narrow on this at all". */
export const ANY = "all";

/**
 * URL facet values → the single value the dropdown shows.
 *
 * `useTableQuery` models every facet as a LIST (the table pages multi-select);
 * these two dropdowns are single-choice, so an empty list is the `ANY`
 * sentinel and anything else is its first entry.
 */
export function facetSelection(values: readonly string[] | undefined): string {
  return values?.[0] ?? ANY;
}

/** The inverse: a dropdown choice → the facet values to write to the URL. */
export function facetValues(selection: string): string[] {
  return selection === ANY ? [] : [selection];
}

export interface TaskFilterState {
  /** The COMMITTED search term (already debounced), matched case-insensitively. */
  searchTerm: string;
  status: string;
  priority: string;
}

/**
 * The in-memory predicate behind `filteredTasks`.
 *
 * Tasks filters and pages client-side — that model is deliberate and unchanged;
 * only the SORT keys travel to the server. Kept a pure function so the
 * "`all` means don't narrow" rule is pinned by a test rather than by reading
 * three chained `&&`s in JSX.
 */
export function matchesTaskFilters(
  task: Pick<Task, "name" | "description" | "status" | "priority">,
  { searchTerm, status, priority }: TaskFilterState,
): boolean {
  const needle = searchTerm.toLowerCase();
  const matchesSearch =
    task.name.toLowerCase().includes(needle) ||
    (task.description?.toLowerCase() ?? "").includes(needle);

  return (
    matchesSearch &&
    (status === ANY || task.status === status) &&
    (priority === ANY || task.priority === priority)
  );
}

/**
 * The "Sort by" dropdown's options, in the order `TASKS_TABLE` lists them.
 *
 * Typed `readonly TableColumn[]` rather than the literal type the `as const`
 * config infers, because those entries carry no `align` and the literal type
 * would not be assignable where a `TableColumn` is expected.
 */
export const TASK_SORT_OPTIONS: readonly TableColumn[] = TASKS_TABLE.columns;

/** The two Tasks views. `?view=` selects between them; the default is the calendar. */
export type TasksView = "calendar" | "list";

/**
 * The href for switching to `view`, KEEPING every other query param.
 *
 * The Calendar/List toggle used to be a bare `/crm/tasks?view=list`, which was
 * harmless only while this page's filters lived in `useState`: the component
 * stays mounted across that same-route navigation, so component state survived
 * a discarded query string. Now that sort, direction, status and priority are
 * all URL state, throwing the query string away silently resets the list to its
 * defaults and refetches — a link to a sorted, filtered view stops surviving one
 * click of the toggle.
 *
 * Both callers go through here (the toggle and the "a search commits, show the
 * list" effect) so there is ONE place that can drop a param, and it is tested.
 */
export function tasksViewHref(searchParams: URLSearchParams, view: TasksView): string {
  const next = new URLSearchParams(searchParams.toString());
  next.set("view", view);
  return `/crm/tasks?${next.toString()}`;
}

/** The view a `?view=` param selects. Anything unrecognised means the default calendar. */
export function tasksViewFrom(searchParams: URLSearchParams): TasksView {
  return searchParams.get("view") === "list" ? "list" : "calendar";
}

/** The `start_date`/`end_date` pair `getCalendarTasks` narrows a fetch with. */
export interface CalendarRange {
  start_date: string;
  end_date: string;
}

/**
 * The visible month as the two `yyyy-MM-dd` bounds the calendar refetch sends.
 *
 * Three handlers on the page (create, toggle-complete, delete) refetch the
 * calendar after a mutation, and all three used to inline
 * `new Date(y, m, 1).toISOString().split("T")[0]`. That expression was wrong
 * twice over:
 *
 * - `toISOString` renders the instant in UTC, but the bounds are built from
 *   LOCAL year/month. East of UTC, local midnight on the 1st is still the
 *   previous month in UTC, so the refetch asked for `2026-08-31` when the user
 *   was looking at September — a whole extra day of tasks, and a range that
 *   disagreed with `task-calendar.tsx`, which already formats the same two
 *   dates with date-fns `format`. Going through `format` makes both agree.
 * - Under `noUncheckedIndexedAccess` the `[0]` is `string | undefined`, which
 *   `exactOptionalPropertyTypes` will not let through an optional-but-not-
 *   undefined `start_date?: string`. `format` returns a plain `string`, so the
 *   assertion the call site would otherwise need disappears with the bug.
 *
 * `now` is injectable so the month arithmetic can be pinned by a test instead
 * of by whatever day the suite happens to run.
 */
export function currentMonthRange(now: Date = new Date()): CalendarRange {
  return {
    start_date: format(new Date(now.getFullYear(), now.getMonth(), 1), "yyyy-MM-dd"),
    end_date: format(new Date(now.getFullYear(), now.getMonth() + 1, 0), "yyyy-MM-dd"),
  };
}
